import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cpApi from "@/lib/clientPortalApi";
import { useCpAuth } from "@/contexts/ClientPortalAuthContext";
import { PageHeader, KpiCard, inrShort, inr, fmtDate, StatusPill } from "./_cpShared";
import { Link } from "react-router-dom";
import { Loader2, FileQuestion, CalendarCheck2 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie, Legend,
} from "recharts";

const TYPE_COLORS = ["#0a2540", "#1d4ed8", "#dc2626"];
const HSN_COLOR = "#0a2540";
const CUSTOMER_COLOR = "#0f766e";

function AnimatedNumber({ value, format = (v) => v, durationMs = 800 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const from = display;
    const to = Number(value) || 0;
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span>{format(Math.round(display))}</span>;
}

export default function CpDashboard() {
  const { user, client } = useCpAuth();
  const navigate = useNavigate();

  // Employees don't have access to the aggregated Overview — bounce them to
  // the first section their permissions allow (or Queries / Compliance which
  // are always available).
  useEffect(() => {
    if (user && user.role !== "company_admin") {
      (async () => {
        try {
          const r = await cpApi.get("/client-portal/dashboard");
          const visible = (r.data?.visible_sections || []);
          // Prefer the first data section the employee can see; otherwise
          // fall back to Queries/RFIs which are always available.
          const SECTION_ORDER = ["sales_b2b", "sales_b2c", "sales_returns",
            "missing_invoices", "gstr1_unfiled", "suspense", "receivables", "payables"];
          const firstSection = SECTION_ORDER.find((s) => visible.includes(s));
          if (firstSection) {
            navigate(`/client-portal/sections/${firstSection}`, { replace: true });
          } else if (visible.includes("compliance")) {
            navigate("/client-portal/compliance", { replace: true });
          } else {
            navigate("/client-portal/queries", { replace: true });
          }
        } catch {
          navigate("/client-portal/queries", { replace: true });
        }
      })();
    }
  }, [user, navigate]);

  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [breakdown, setBreakdown] = useState({ by_type: [], by_hsn: [], by_customer: [] });
  const [loading, setLoading] = useState(true);

  // Drill-down handlers — navigate to Sales B2B section pre-filtered.
  const drillToHsn = (hsn) => {
    if (!hsn) return;
    navigate(`/client-portal/sections/sales_b2b?filter_col=hsn_sac&filter_val=${encodeURIComponent(hsn)}`);
  };
  const drillToCustomer = (name) => {
    if (!name) return;
    navigate(`/client-portal/sections/sales_b2b?filter_col=customer_billing_name&filter_val=${encodeURIComponent(name)}`);
  };

  useEffect(() => {
    // Skip the heavy data fetch for employees — they're being redirected.
    if (user && user.role !== "company_admin") {
      setLoading(false);
      return;
    }
    let on = true;
    (async () => {
      try {
        const [d, m, b] = await Promise.all([
          cpApi.get("/client-portal/dashboard"),
          cpApi.get("/client-portal/sales/monthly").catch(() => ({ data: [] })),
          cpApi.get("/client-portal/sales/breakdown").catch(() => ({ data: { by_type: [], by_hsn: [], by_customer: [] } })),
        ]);
        if (!on) return;
        setData(d.data);
        setMonthly(m.data || []);
        setBreakdown(b.data || { by_type: [], by_hsn: [], by_customer: [] });
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-[300px] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0a2540]" size={28} />
      </div>
    );
  }

  const totalSales = data?.total_sales || 0;
  const b2b = data?.sales_b2b || {};
  const b2c = data?.sales_b2c || {};
  const returns = data?.sales_returns || {};
  const potentialLoss = data?.potential_loss_gstr1 || { total: 0, central_tax: 0, state_ut_tax: 0, integrated_tax: 0, invoice_count: 0 };
  const receivablesPending = (data?.receivables?.by_status?.Pending?.total || 0)
                           + (data?.receivables?.by_status?.Partial?.total || 0);
  const payablesPending = (data?.payables?.by_status?.Pending?.total || 0)
                        + (data?.payables?.by_status?.Partial?.total || 0);
  const upcoming = data?.upcoming_compliance || [];
  const openRfis = data?.open_rfis_count || 0;
  const monthLabel = new Date().toLocaleString("en-IN", { month: "long", year: "numeric" });

  // Last MoM growth indicator: compare last vs prev month if available
  let momPct = null;
  if (monthly.length >= 2) {
    const prev = monthly[monthly.length - 2]?.amount || 0;
    const last = monthly[monthly.length - 1]?.amount || 0;
    if (prev > 0) momPct = ((last - prev) / prev) * 100;
  }

  const positiveTypes = (breakdown.by_type || []).filter((t) => t.value > 0);

  return (
    <div>
      <PageHeader subtitle={`Welcome, ${user?.name?.split(" ")[0] || "there"}`} title={`${client?.name || ""} Overview`} />

      <div className="p-4 sm:p-8 space-y-6">
        {/* TOTAL SALES — featured with animated counter */}
        <div className="bg-gradient-to-br from-[#0a2540] via-[#142b4a] to-[#1d4ed8] text-white p-6 rounded-sm relative overflow-hidden" data-testid="cp-total-sales">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/5 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] tracking-[0.22em] uppercase text-slate-300 mb-2 flex items-center gap-3">
              <span>Total Sales</span>
              {momPct != null && (
                <span className={`text-[10px] tracking-normal normal-case px-2 py-0.5 rounded-sm font-semibold ${momPct >= 0 ? "bg-emerald-500/20 text-emerald-200" : "bg-red-500/20 text-red-200"}`}>
                  {momPct >= 0 ? "▲" : "▼"} {Math.abs(momPct).toFixed(1)}% MoM
                </span>
              )}
            </div>
            <div className="text-3xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              <AnimatedNumber value={totalSales} format={(v) => inr(v)} />
            </div>
            <div className="mt-2 text-[11px] text-slate-300">
              = B2B {inrShort(b2b.total_taxable_value || 0)}
              <span className="text-emerald-300 mx-1">+</span>
              B2C {inrShort(b2c.total_taxable_value || 0)}
              <span className="text-red-300 mx-1">−</span>
              Returns {inrShort(returns.total_taxable_value || 0)}
            </div>
          </div>
        </div>

        {/* POTENTIAL LOSS — GSTR-1 Unfiled invoices */}
        {potentialLoss.invoice_count > 0 && (
          <div
            className="bg-gradient-to-br from-red-50 to-amber-50 border-l-4 border-red-600 p-5 rounded-sm flex items-center gap-5 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="cp-potential-loss"
            onClick={() => navigate("/client-portal/sections/gstr1_unfiled")}
          >
            <div className="h-14 w-14 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0 text-2xl">⚠</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-red-700 font-semibold">Potential Loss — GSTR-1 Unfiled by Suppliers</div>
              <div className="text-2xl sm:text-3xl font-bold tracking-tight text-red-700 mt-1" style={{ fontFamily: "Outfit" }}>
                <AnimatedNumber value={potentialLoss.total} format={(v) => inr(v)} />
              </div>
              <div className="text-[11px] text-slate-600 mt-1.5">
                ITC at risk across {potentialLoss.invoice_count} invoice{potentialLoss.invoice_count === 1 ? "" : "s"} ·
                <span className="ml-1">CGST {inrShort(potentialLoss.central_tax)}</span>
                <span className="mx-1">+</span>
                <span>SGST {inrShort(potentialLoss.state_ut_tax)}</span>
                <span className="mx-1">+</span>
                <span>IGST {inrShort(potentialLoss.integrated_tax)}</span>
              </div>
            </div>
            <div className="text-xs text-red-700 font-semibold tracking-wide shrink-0 hidden sm:block">VIEW &rarr;</div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Sales B2B" value={inrShort(b2b.total_taxable_value || 0)} hint={`${b2b.count || 0} invoices`} accent="blue" testid="kpi-b2b" />
          <KpiCard label="Sales B2C" value={inrShort(b2c.total_taxable_value || 0)} hint={`${b2c.count || 0} invoices`} accent="green" testid="kpi-b2c" />
          <KpiCard label="Sales Returns" value={inrShort(returns.total_taxable_value || 0)} hint={`${returns.count || 0} credit notes`} accent="amber" testid="kpi-returns" />
          <KpiCard label="Open Queries" value={openRfis} hint="From Griffin" accent="red" testid="kpi-rfis" />
        </div>

        {/* Receivables / Payables strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <KpiCard label="Receivables Pending" value={inrShort(receivablesPending)} hint="Money owed to you" accent="amber" testid="kpi-receivables" />
          <KpiCard label="Payables Pending" value={inrShort(payablesPending)} hint="Money you owe" accent="red" testid="kpi-payables" />
        </div>

        {/* Monthly sales + Sales Type donut */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard title="Monthly Sales Trend" subtitle="B2B + B2C − Returns" colSpan="lg:col-span-2" testid="chart-monthly">
            {monthly.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => inrShort(v)} />
                  <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="amount" name="Net Sales" radius={[6, 6, 0, 0]} animationDuration={700}>
                    {monthly.map((row, i) => <Cell key={i} fill={row.amount >= 0 ? "#0a2540" : "#dc2626"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Sales by Type" subtitle="B2B vs B2C vs Returns" testid="chart-by-type">
            {positiveTypes.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={positiveTypes}
                    dataKey="value"
                    nameKey="label"
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={3}
                    animationBegin={100} animationDuration={900}
                  >
                    {positiveTypes.map((_, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Top HSN + Top Customer side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Top HSN / SAC" subtitle="By taxable value · click to drill down" testid="chart-by-hsn">
            {(breakdown.by_hsn || []).length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={breakdown.by_hsn} layout="vertical" margin={{ top: 5, right: 25, left: 5, bottom: 5 }}>
                  <CartesianGrid stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => inrShort(v)} />
                  <YAxis dataKey="label" type="category" tick={{ fontSize: 10, fill: "#0a2540" }} width={70} />
                  <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="value" fill={HSN_COLOR} radius={[0, 4, 4, 0]} animationDuration={700}
                    cursor="pointer"
                    onClick={(e) => drillToHsn(e?.label)} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Top Customers" subtitle="By taxable value · click to drill down" testid="chart-by-customer">
            {(breakdown.by_customer || []).length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={breakdown.by_customer} layout="vertical" margin={{ top: 5, right: 25, left: 5, bottom: 5 }}>
                  <CartesianGrid stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => inrShort(v)} />
                  <YAxis dataKey="label" type="category" tick={{ fontSize: 10, fill: "#0f766e" }} width={120} />
                  <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="value" fill={CUSTOMER_COLOR} radius={[0, 4, 4, 0]} animationDuration={700}
                    cursor="pointer"
                    onClick={(e) => drillToCustomer(e?.label)} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Compliance current month + Queries */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 p-5 rounded-sm" data-testid="cp-card-upcoming-compliance">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                  <CalendarCheck2 size={14} /> Compliance — {monthLabel}
                </h3>
                <p className="text-[10px] text-slate-500 mt-0.5">Due this month only</p>
              </div>
              <Link to="/client-portal/compliance" className="text-xs text-[#0a2540] hover:underline">Open calendar →</Link>
            </div>
            {upcoming.length === 0 ? (
              <div className="text-xs text-slate-400 py-6 text-center">No compliances due in {monthLabel}.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcoming.map((c) => (
                  <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{c.title}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{c.category || "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-slate-700">{fmtDate(c.due_date)}</div>
                      <StatusPill status={c.status || "Upcoming"} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                <FileQuestion size={14} /> Queries
              </h3>
              <Link to="/client-portal/queries" className="text-xs text-[#0a2540] hover:underline">All →</Link>
            </div>
            <div className="text-3xl font-bold text-[#0a2540] my-3" style={{ fontFamily: "Outfit" }}>
              <AnimatedNumber value={openRfis} />
            </div>
            <div className="text-xs text-slate-500">
              {openRfis === 0 ? "All caught up." : `open quer${openRfis === 1 ? "y" : "ies"} from Griffin.`}
            </div>
          </div>
        </div>

        {/* Receivables / Payables breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatusBreakdownCard title="Receivables" data={data?.receivables?.by_status || {}} />
          <StatusBreakdownCard title="Payables" data={data?.payables?.by_status || {}} />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, colSpan = "", testid }) {
  return (
    <div className={`bg-white border border-slate-200 p-5 rounded-sm ${colSpan}`} data-testid={testid}>
      <div className="mb-3">
        <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>{title}</h3>
        {subtitle && <div className="text-[10px] text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function EmptyChart() {
  return <div className="text-xs text-slate-400 py-12 text-center">No data yet.</div>;
}

function StatusBreakdownCard({ title, data }) {
  const rows = Object.entries(data || {});
  return (
    <div className="bg-white border border-slate-200 p-5 rounded-sm">
      <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: "Outfit" }}>{title} by status</h3>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">No data.</div>
      ) : (
        <ul className="space-y-2">
          {rows.map(([status, v]) => (
            <li key={status} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2">
                <StatusPill status={status} />
                <span className="text-slate-500">({v.count})</span>
              </span>
              <span className="font-mono font-semibold">{inrShort(v.total)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
